import type { LinearComponentPatternDefinition } from './LinearComponentPatternTypes';

/** 线性零部件阵列定义持久化协议版本。 */
export const LinearComponentPatternPersistenceSchemaVersion = {
  /** 首个定义型协议：只保存特征标识、名称与可重新求解的定义。 */
  V1: 1,
} as const;

/** 线性零部件阵列定义持久化协议版本类型。 */
export type LinearComponentPatternPersistenceSchemaVersion =
  typeof LinearComponentPatternPersistenceSchemaVersion[keyof typeof LinearComponentPatternPersistenceSchemaVersion];

/** 单个线性零部件阵列特征的持久化记录。 */
export interface LinearComponentPatternPersistenceRecord {
  /** 特征稳定标识。 */
  readonly featureId: string;
  /** 设计树显示名称。 */
  readonly displayName: string;
  /** 阵列定义所属的装配 occurrence；旧 V1 文档可以缺省。 */
  readonly ownerAssemblyNodeId?: string;
  /** 可在加载后重新求解阵列实例的完整定义。 */
  readonly definition: LinearComponentPatternDefinition;
}

/** 线性零部件阵列定义持久化文档。 */
export interface LinearComponentPatternPersistenceDocument {
  /** 协议版本。 */
  readonly schemaVersion: typeof LinearComponentPatternPersistenceSchemaVersion.V1;
  /** 按创建顺序保存的阵列特征定义。 */
  readonly records: readonly LinearComponentPatternPersistenceRecord[];
}

/**
 * 深克隆线性阵列定义持久化文档。
 *
 * 该文档只允许 plain data，因此 JSON 深克隆同时切断运行时引用并保证导出结果可序列化。
 *
 * @param document 来源文档。
 * @returns 独立的定义文档副本。
 */
export function cloneLinearComponentPatternPersistenceDocument(
  document: LinearComponentPatternPersistenceDocument,
): LinearComponentPatternPersistenceDocument {
  return JSON.parse(JSON.stringify(document)) as LinearComponentPatternPersistenceDocument;
}

/**
 * 解析并校验线性阵列定义持久化文档。
 *
 * @param input 外部输入。
 * @returns 已校验且与输入引用隔离的文档。
 * @throws 当协议版本、记录标识或定义结构无效时抛出可定位错误。
 */
export function parseLinearComponentPatternPersistenceDocument(
  input: unknown,
): LinearComponentPatternPersistenceDocument {
  if (!isRecord(input)) {
    throw new Error('线性零部件阵列定义文档必须是对象。');
  }
  if (input.schemaVersion !== LinearComponentPatternPersistenceSchemaVersion.V1) {
    throw new Error(`不支持的线性零部件阵列定义协议版本：${String(input.schemaVersion)}。`);
  }
  if (!Array.isArray(input.records)) {
    throw new Error('线性零部件阵列定义文档 records 必须是数组。');
  }

  const featureIds = new Set<string>();
  input.records.forEach((record, index) => {
    if (!isRecord(record)) {
      throw new Error(`线性零部件阵列定义 records[${index}] 必须是对象。`);
    }
    assertNonEmptyString(record.featureId, `records[${index}].featureId`);
    assertNonEmptyString(record.displayName, `records[${index}].displayName`);
    if (record.ownerAssemblyNodeId !== undefined) {
      assertNonEmptyString(record.ownerAssemblyNodeId, `records[${index}].ownerAssemblyNodeId`);
    }
    if (featureIds.has(record.featureId)) {
      throw new Error(`线性零部件阵列定义存在重复 featureId：${record.featureId}。`);
    }
    featureIds.add(record.featureId);

    if (!isRecord(record.definition)) {
      throw new Error(`records[${index}].definition.state 必须是对象。`);
    }
    const definition = record.definition;
    if (!isRecord(definition.state)) {
      throw new Error(`records[${index}].definition.state 必须是对象。`);
    }
    const state = definition.state;
    assertStringArray(state.componentObjectIds, `records[${index}].definition.state.componentObjectIds`);
    assertStringArray(state.skippedInstanceKeys, `records[${index}].definition.state.skippedInstanceKeys`);
    assertStringArray(state.modifiedInstanceObjectIds, `records[${index}].definition.state.modifiedInstanceObjectIds`);
  });

  return cloneLinearComponentPatternPersistenceDocument(
    input as unknown as LinearComponentPatternPersistenceDocument,
  );
}

/**
 * 断言未知值是非空字符串。
 *
 * @param value 待校验值。
 * @param fieldName 字段路径。
 */
function assertNonEmptyString(value: unknown, fieldName: string): asserts value is string {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new Error(`${fieldName} 必须是非空字符串。`);
  }
}

/**
 * 断言未知值是字符串数组。
 *
 * @param value 待校验值。
 * @param fieldName 字段路径。
 */
function assertStringArray(value: unknown, fieldName: string): asserts value is string[] {
  if (!Array.isArray(value) || value.some((item) => typeof item !== 'string')) {
    throw new Error(`${fieldName} 必须是字符串数组。`);
  }
}

/**
 * 判断未知值是否为普通记录对象。
 *
 * @param value 待判断值。
 * @returns 是否为普通记录对象。
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}
